import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import PropTypes from "prop-types";
import axios from "axios";
import { FormattedMessage } from "react-intl";
import { useRollbar } from "@rollbar/react";
import Loading from "../../components/Loading";
import { Icon } from "../../components";

const VIMEO_OPTIONS = {
  background: false,
  byline: false,
  transparent: true,
  responsive: true,
};

const LoadingWrapper = styled.div`
  padding: 25%;
  text-align: center;
  background-color: ${props => props.theme.light};
  border-radius: ${props => props.theme.borderRadius};
`;

const Wrapper = styled.div`
  & > iframe {
    border: 0;
    outline: 0;
    margin: 1rem 0;
  }

  @media (${props => props.theme.tabletScreen}) {
    & > iframe {
      margin: 1rem;
    }
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & > i {
    margin-right: 0.5rem;
  }
`;

const VimeoVideo = ({ link }) => {
  const [oEmbed, setOEmbed] = useState(null);
  const [error, setError] = useState(null);
  const rollbar = useRollbar();

  useEffect(() => {
    if (!link) {
      return setOEmbed(null);
    }

    const params = Object.keys(VIMEO_OPTIONS)
      .reduce(function(a, k) {
        a.push(k + "=" + encodeURIComponent(VIMEO_OPTIONS[k]));
        return a;
      }, [])
      .join("&");
    let unmounted = false;
    let source = axios.CancelToken.source();

    axios
      .get(`https://vimeo.com/api/oembed.json?url=${link}?${params}`, {
        cancelToken: source.token,
      })
      .then(response => {
        if (unmounted) {
          console.log("Component unmounted, ignoring Vimeo oEmbed result");
          return;
        }

        setOEmbed(response.data);
      })
      .catch(error => {
        if (axios.isCancel(error)) {
          return;
        }

        if (error?.isAxiosError) {
          setError(error.response.status);
        } else {
          setError(500);
        }
        rollbar.error("Failed to fetch Vimeo oEmbed", { error, link });
      });

    return () => {
      unmounted = true;
      source.cancel("Component unmounted, canceling Vimeo oEmbed request");
    };
  }, [link, setOEmbed, rollbar]);

  if (oEmbed) {
    return <Wrapper dangerouslySetInnerHTML={{ __html: oEmbed.html }}></Wrapper>;
  }

  return (
    <LoadingWrapper>
      {error ? (
        <ErrorMessage>
          <Icon name="times" color="danger" />
          <FormattedMessage id={error === 404 ? "videoNotFound" : "error"} />
        </ErrorMessage>
      ) : (
        <Loading text={<FormattedMessage id="loading" />} />
      )}
    </LoadingWrapper>
  );
};

VimeoVideo.propTypes = {
  link: PropTypes.string.isRequired,
};

export default VimeoVideo;
